import { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { memo } from 'react'
import { sidebarMenuList } from '../../utils/sidebarMenuList'
import { getCurrentUser } from '../../utils/helper/currentUser'

const Sidebar = () => {
  const location = useLocation()
  const [activeItem, setActiveItem] = useState(null)
  const timeoutRef = useRef<number | null>(null)
  const currentUser = getCurrentUser()

  // Function to handle mouse enter on parent item
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMouseEnter = (index: any) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setActiveItem(index)
  }

  // Function to handle mouse leave with delay
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveItem(null)
    }, 300) // 300ms delay before hiding menu
  }

  // Clean up timeout on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    },
    [],
  )

  return (
    <div className="fixed z-10 flex h-screen w-20 flex-col items-center bg-white p-2 pt-20 shadow-md">
      {sidebarMenuList.map((item, index) => {
        // Show item if:
        // 1. User has role 1 (admin) - can access all items
        // 2. User doesn't have role 1 but item is not admin-only
        const canAccess = currentUser?.role === 1 || !item.isAdminOnly

        return canAccess ? (
          <div
            key={index}
            className="relative mb-3 w-full"
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
          >
            <Link to={item.path} className="block w-full">
              <div
                className={`flex flex-col items-center justify-center rounded-lg p-2 hover:bg-slate-100 ${
                  location.pathname === item.path ? 'bg-blue-100 text-blue-600' : 'text-gray-700'
                }`}
              >
                <div className="mb-1 text-xl">{item.icon}</div>
                <p className="text-center text-xs">{item.title}</p>
              </div>
            </Link>

            {/* Floating Child Items with improved interaction */}
            {item.childs.length > 0 && activeItem === index && (
              <div
                className="absolute left-20 top-0 z-20 w-48 rounded-lg bg-white shadow-lg"
                onMouseEnter={() => handleMouseEnter(index)} // Keep menu open when hovering on submenu
                onMouseLeave={handleMouseLeave}
              >
                <div className="absolute left-[-10px] top-0 h-full w-10"></div>
                {item.childs.map((child, childIndex) => (
                  <Link to={child.path} key={childIndex}>
                    <div
                      className={`flex items-center justify-between px-4 py-3 text-sm hover:bg-slate-100 ${
                        location.pathname === child.path
                          ? 'bg-blue-50 font-medium text-blue-600'
                          : 'text-gray-600'
                      }`}
                    >
                      <span>{child.title}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : null
      })}
    </div>
  )
}

export default memo(Sidebar)
